import { motion, useReducedMotion } from 'framer-motion';

interface TrailWalkerProps {
  x: number;
  y: number;
  progress: number; // 0 to 1, how far along the trail
  totalSteps: number;
  currentStep: number;
}

/** Colors that fill in progressively as the student advances */
const PROGRESS_COLORS = [
  'hsl(var(--primary) / 0.2)',   // step 1-2: faint
  'hsl(var(--primary) / 0.35)',  // step 3-4
  'hsl(var(--primary) / 0.5)',   // step 5-6
  'hsl(var(--primary) / 0.65)',  // step 7-8
  'hsl(var(--primary) / 0.8)',   // step 9-10
  'hsl(var(--primary))',         // step 11-12: full
];

function getProgressColor(step: number, total: number): string {
  const idx = Math.min(
    Math.floor((step / Math.max(total - 1, 1)) * (PROGRESS_COLORS.length - 1)),
    PROGRESS_COLORS.length - 1
  );
  return PROGRESS_COLORS[idx];
}

const TrailWalker = ({ x, y, progress, totalSteps, currentStep }: TrailWalkerProps) => {
  const prefersReducedMotion = useReducedMotion();
  const noRepeat = prefersReducedMotion ? 0 : Infinity;
  const color = getProgressColor(currentStep, totalSteps);
  const glowColor = 'hsl(var(--primary) / 0.3)';

  return (
    <motion.g
      animate={{ x, y: y - 28 }}
      transition={{ type: 'spring', stiffness: 80, damping: 18 }}
    >
      {/* Ground glow */}
      <motion.ellipse
        cx={0}
        cy={28}
        rx={10}
        ry={3}
        fill={glowColor}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 2, repeat: noRepeat }}
      />

      {/* Human silhouette — simple stylized figure */}
      <g>
        {/* Head */}
        <motion.circle
          cx={0}
          cy={2}
          r={5}
          fill={color}
          stroke="hsl(var(--primary))"
          strokeWidth={1}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: noRepeat, ease: 'easeInOut' }}
        />

        {/* Body */}
        <motion.path
          d="M 0 7 L 0 18"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          fill="none"
        />

        {/* Arms — animated walking motion */}
        <motion.path
          d="M -6 11 L 0 13 L 6 11"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          fill="none"
          animate={{ d: ['M -6 11 L 0 13 L 6 11', 'M -5 13 L 0 12 L 5 13', 'M -6 11 L 0 13 L 6 11'] }}
          transition={{ duration: 1.5, repeat: noRepeat, ease: 'easeInOut' }}
        />

        {/* Legs — animated walking motion */}
        <motion.path
          d="M 0 18 L -5 26 M 0 18 L 5 26"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          fill="none"
          animate={{
            d: [
              'M 0 18 L -5 26 M 0 18 L 5 26',
              'M 0 18 L -3 27 M 0 18 L 4 25',
              'M 0 18 L -5 26 M 0 18 L 5 26',
            ],
          }}
          transition={{ duration: 1.2, repeat: noRepeat, ease: 'easeInOut' }}
        />

        {/* Progress fill overlay — body glows brighter as progress increases */}
        <motion.circle
          cx={0}
          cy={12}
          r={8}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={0.5}
          opacity={progress * 0.4}
        />
      </g>

      {/* Step indicator above head */}
      <motion.text
        y={-8}
        textAnchor="middle"
        className="text-[8px] font-bold"
        fill="hsl(var(--primary))"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {currentStep + 1}/{totalSteps}
      </motion.text>
    </motion.g>
  );
};

export default TrailWalker;
